import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  LinearProgress,
  useTheme,
  Stack,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import UpdateIcon from "@mui/icons-material/Update";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { motion, animate } from "framer-motion";
import { useEffect, useRef } from "react";
import { computeSLA } from "../../config/sla";
import { alpha } from "@mui/material/styles";

const MotionCard = motion.create ? motion.create(Card) : motion(Card);
const MotionBox = motion.create ? motion.create(Box) : motion(Box);

/* ---------------- Progress Number ---------------- */
const ProgressNumber = ({ value }) => {
  const ref = useRef(null);
  const prevValue = useRef(value);

  useEffect(() => {
    if (prevValue.current === value) return;
    prevValue.current = value;

    const controls = animate(0, value, {
      duration: 1.5,
      onUpdate: (v) => {
        if (ref.current) {
          ref.current.textContent = `${v.toFixed(0)}%`;
        }
      },
    });

    return () => controls.stop();
  }, [value]);

  return (
    <Typography
      ref={ref}
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: "#64748B",
        minWidth: "32px",
        textAlign: "center",
      }}
    >
      {`${Math.round(value)}%`}
    </Typography>
  );
};

/* ---------------- Summary Item ---------------- */
const SummaryItem = ({
  label,
  value,
  icon,
  iconBg,
  iconColor,
  delay,
  children,
}) => (
  <MotionBox
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.4, delay, ease: "easeOut" }}
    whileHover={{ scale: 1.01 }}
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      p: 2,
      borderRadius: 3,
      bgcolor: "#FAFAFA",
      border: "1px solid #F1F5F9",
      "&:hover": {
        bgcolor: "#F8FAFC",
        borderColor: "#E2E8F0",
        boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
      },
    }}
  >
    <Avatar
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2.5,
        bgcolor: iconBg,
        color: iconColor,
        flexShrink: 0,
        "& svg": { fontSize: 22 },
      }}
    >
      {icon}
    </Avatar>

    <Box sx={{ flex: 1 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          color: "#94A3B8",
          textTransform: "uppercase",
          letterSpacing: 0.8,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: "#1E293B",
        }}
      >
        {value}
      </Typography>

      {children}
    </Box>
  </MotionBox>
);

/* ---------------- Main Component ---------------- */
export default function LeadSummary({ lead = {}, maxSla }) {
  const theme = useTheme();

  const resolvedMaxSla =
    maxSla ?? lead?.call_policy?.sla_first_response_minutes;

  const slaState = computeSLA({
    lead,
    targetMinutes: resolvedMaxSla,
    theme,
  });

  const percent = slaState.progress ?? 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  /* ✅ FIRST CONTACT SOURCE (ONLY CHANGE) */
  const firstContactAt =
    lead?.sla?.first_contact_at || lead?.first_contact_at;

  return (
    <MotionCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{
        borderRadius: 4,
        bgcolor: "#FFFFFF",
        boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
        border: "1px solid #F1F5F9",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 700,
            mb: 2.5,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Lead Summary
        </Typography>

        <Stack spacing={2}>
          <SummaryItem
            label="Lead Created"
            value={formatDate(lead.created_at)}
            icon={<AccessTimeIcon />}
            iconBg="rgba(59,130,246,0.12)"
            iconColor="#3B82F6"
            delay={0.1}
          />

          <SummaryItem
            label="Last Updated"
            value={formatDate(lead.updatedAt)}
            icon={<UpdateIcon />}
            iconBg="rgba(16,185,129,0.12)"
            iconColor="#10B981"
            delay={0.2}
          />

          <SummaryItem
            label="First Contact Delay"
            value={
              firstContactAt
                ? `First contact: ${formatDate(firstContactAt)}`
                : "Not contacted yet"
            }
            icon={<HourglassEmptyIcon />}
            iconBg={alpha(theme.palette.primary.main, 0.12)}
            iconColor={theme.palette.primary.main}
            delay={0.3}
          >
            {resolvedMaxSla && (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={percent}
                    sx={{
                      flex: 1,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: "#E2E8F0",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 3,
                        background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                      },
                    }}
                  />
                  <ProgressNumber value={percent} />
                </Box>
              </Box>
            )}
          </SummaryItem>
        </Stack>
      </CardContent>
    </MotionCard>
  );
}
